import React from 'react';
import { LockIcon, ChevronLeftIcon, ChevronRightIcon, RotateCwIcon } from 'lucide-react';
interface BrowserFrameProps {
  url: string;
  children: React.ReactNode;
  className?: string;
  /** Compact chrome for the small side-by-side site mocks. */
  dense?: boolean;
  /** Drops the frame's own radius, border and shadow for full-bleed use. */
  bare?: boolean;
  /**
   * Renders the page at a larger logical size and scales it down, so a
   * full-screen demo reads like a real browser at normal zoom rather than a
   * magnified mock.
   */
  zoom?: number;
}

/**
 * Neutral browser chrome for the "traditional website" scenes. Deliberately
 * plain so the friction inside the page is what the audience reads.
 */
export function BrowserFrame({
  url,
  children,
  className = '',
  dense = false,
  bare = false,
  zoom = 1
}: BrowserFrameProps) {
  return <div className={`flex flex-col overflow-hidden bg-white ${bare ? '' : 'rounded-2xl border border-slate-200/80'} ${className}`} style={bare ? undefined : {
    boxShadow: '0 24px 60px -24px rgba(11,18,32,0.30)'
  }} data-id="element-1712" data-mp="39ko96m7aqv28">
      <div className={`flex items-center gap-2.5 shrink-0 bg-slate-50 border-b border-slate-100 ${dense ? 'h-7 px-2.5' : 'h-9 px-3.5'}`} data-id="element-1713" data-mp="17gggs1lrex1c">
        <div className="flex items-center gap-1.5" data-id="element-1714" data-mp="1hfyzyftuiesc">
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1715" data-mp="1hg7fygl9m07r" />
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1716" data-mp="tpbqhq4a7h3o" />
          <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1717" data-mp="tnqxi4xm867x" />
        </div>
        {!dense && <div className="flex items-center gap-1 text-slate-300" data-id="element-1718" data-mp="12ieyhiw5r1qx">
            <ChevronLeftIcon className="w-3.5 h-3.5" data-id="element-1719" data-mp="12jzw5hdrr0t0" />
            <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1720" data-mp="xr6niif6j8bn" />
            <RotateCwIcon className="w-3 h-3" data-id="element-1721" data-mp="xplsyssv7pds" />
          </div>}
        <div className={`flex items-center justify-center gap-1.5 mx-auto rounded-md bg-white border border-slate-200 text-slate-400 ${dense ? 'h-4 px-2 text-[8px] w-[120px]' : 'h-5 px-3 text-[10px] w-[200px]'}`} data-id="element-1722" data-mp="a0t57jmecnu9">
          <LockIcon className={dense ? 'w-2 h-2' : 'w-2.5 h-2.5'} data-id="element-1723" data-mp="2a43ujnxdrz0q" />
          <span className="truncate" data-id="element-1724" data-mp="2a2akrzrh0yoi">{url}</span>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 overflow-hidden bg-white" data-id="element-1725" data-mp="2ewerchwtr4p2">
        {zoom === 1 ? children : <div className="absolute top-0 left-0" style={{
        width: `${100 / zoom}%`,
        height: `${100 / zoom}%`,
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }} data-id="element-1726" data-mp="2jw164vsawszz">
            {children}
          </div>}
      </div>
    </div>;
}